import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Ban } from 'lucide-react';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import {
  MagicLinkStatusCard,
  TokenErrorView,
} from '~/components/magic-link/MagicLinkStatusCard';
import { useTokenLoadedOnce } from '~/hooks/useTokenLoadedOnce';
import {
  getByToken,
  postByToken,
  tokenErrorCodeOf,
  type TokenErrorCode,
} from '~/lib/magicLinkToken';
import {
  PriceNegotiationResponseForm,
  type PriceNegotiationCommonData,
  type PriceNegotiationData,
  type PriceNegotiationResponseValues,
  type PriceNegotiationTallyRecipeLine,
} from '~/pages/respond-to-price-negotiation/PriceNegotiationResponseForm';

type PageState =
  | { status: 'success'; action: 'responded' | 'declined' }
  | { status: 'cancelled' };

async function fetchPriceNegotiation(
  token: string
): Promise<PriceNegotiationData> {
  const data = await getByToken<
    PriceNegotiationCommonData & {
      tallyRecipe: PriceNegotiationTallyRecipeLine[] | null;
    }
  >(`/api/price-negotiations/by-token?token=${encodeURIComponent(token)}`);
  return data.tallyRecipe
    ? { ...data, isTally: true, tallyRecipe: data.tallyRecipe }
    : { ...data, isTally: false };
}

function respondByToken(
  token: string,
  offeredPrice: number,
  sellerNote: string | null
): Promise<void> {
  return postByToken('/api/price-negotiations/respond-by-token', {
    token,
    offeredPrice,
    sellerNote,
  });
}

function declineByToken(token: string): Promise<void> {
  return postByToken('/api/price-negotiations/decline-by-token', { token });
}

export function RespondToPriceNegotiation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const load = useTokenLoadedOnce(token, fetchPriceNegotiation);
  const [state, setState] = useState<PageState | null>(null);
  const [submitError, setSubmitError] = useState<TokenErrorCode | null>(null);
  const { t } = useTranslation('respond-to-price-negotiation');

  if (!token) {
    return (
      <TokenErrorView
        code="invalid"
        namespace="respond-to-price-negotiation"
        keyPrefix="respond_to_price_negotiation"
        testId="respond-price-error"
      />
    );
  }

  if (load.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neut-bg-alt">
        <PageLoadingSpinner name={t('respond_to_price_negotiation.loading')} />
      </div>
    );
  }

  if (load.status === 'error' || submitError) {
    const code =
      submitError ?? (load.status === 'error' ? load.code : 'unknown');
    return (
      <TokenErrorView
        code={code}
        namespace="respond-to-price-negotiation"
        keyPrefix="respond_to_price_negotiation"
        testId="respond-price-error"
      />
    );
  }

  if (state?.status === 'success') {
    return <SuccessView action={state.action} />;
  }

  if (state?.status === 'cancelled') {
    return <CancelledView />;
  }

  return (
    <ResponseForm
      data={load.data}
      token={token}
      onSuccess={(action) => setState({ status: 'success', action })}
      onCancel={() => setState({ status: 'cancelled' })}
      onError={setSubmitError}
    />
  );
}

function ResponseForm({
  data,
  token,
  onSuccess,
  onCancel,
  onError,
}: {
  data: PriceNegotiationData;
  token: string;
  onSuccess: (action: 'responded' | 'declined') => void;
  onCancel: () => void;
  onError: (code: TokenErrorCode) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const handleSubmit = async (values: PriceNegotiationResponseValues) => {
    if (submittedRef.current || submitting) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await respondByToken(token, values.offeredPrice, values.sellerNote);
      onSuccess('responded');
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      onError(tokenErrorCodeOf(err));
    }
  };

  const handleDecline = async () => {
    if (submittedRef.current || submitting) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await declineByToken(token);
      onSuccess('declined');
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      onError(tokenErrorCodeOf(err));
    }
  };

  return (
    <div className="min-h-screen bg-neut-bg-alt py-8 px-4 w-full">
      <PriceNegotiationResponseForm
        data={data}
        submitting={submitting}
        onSubmit={handleSubmit}
        onDecline={handleDecline}
        onCancel={onCancel}
      />
    </div>
  );
}

function SuccessView({ action }: { action: 'responded' | 'declined' }) {
  const { t } = useTranslation('respond-to-price-negotiation');
  const isDecline = action === 'declined';
  return (
    <MagicLinkStatusCard
      testId="respond-price-success"
      icon={<CheckCircle className="text-success-500" size={48} />}
      title={
        isDecline
          ? t('respond_to_price_negotiation.success.declined_title')
          : t('respond_to_price_negotiation.success.responded_title')
      }
      message={
        isDecline
          ? t('respond_to_price_negotiation.success.declined_message')
          : t('respond_to_price_negotiation.success.responded_message')
      }
    />
  );
}

function CancelledView() {
  const { t } = useTranslation('respond-to-price-negotiation');
  return (
    <MagicLinkStatusCard
      testId="respond-price-cancelled"
      icon={<Ban className="text-neut-subtle" size={48} />}
      title={t('respond_to_price_negotiation.cancelled.title')}
      message={t('respond_to_price_negotiation.cancelled.message')}
    />
  );
}
